import React, { useState, useCallback, useEffect } from "react";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import { CalendarOutlined } from "@ant-design/icons";
import "react-datepicker/dist/react-datepicker.css";
import "./CustomDatePicker.css";
import es from "date-fns/locale/es";
import PropTypes from "prop-types";

registerLocale("es", es);

CustomDatePicker.propTypes = {
  onChange: PropTypes.func,
  onClickOutside: PropTypes.func,
  selected: PropTypes.object,
  locale: PropTypes.string,
  style: PropTypes.object,
};

export default function CustomDatePicker({
  onChange,
  onClickOutside,
  selected,
  ...props
}) {
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    if (props.locale) {
      setDefaultLocale(props.locale);
    }
  }, [props.locale]);

  useEffect(() => {
    if (selected !== null && selected !== undefined) {
      setDate(selected);
    }
  }, [selected]);

  const isLeapYear = useCallback((year) => {
    return !((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0);
  }, []);

  const isValidFirstChar = useCallback((char) => {
    return ["0", "1"].includes(char);
  }, []);

  const isValidSecondChar = useCallback((firstChar, secondChar) => {
    if (firstChar === "0") {
      return ["0", "1", "2"].includes(secondChar);
    }
    return true;
  }, []);

  const isValidThirdChar = useCallback((month, char) => {
    if (month === "02") {
      return ["0", "1", "2"].includes(char);
    }
    return ["0", "1", "2", "3"].includes(char);
  }, []);

  const isValidDayForMonth = useCallback((month, day) => {
    if (day === "00") return false;
    if (day[0] === "3" && !["0", "1"].includes(day[1])) return false;
    if (
      day[0] === "3" &&
      day[1] === "1" &&
      !["01", "03", "05", "07", "08", "10", "12"].includes(month)
    )
      return false;
    return true;
  }, []);

  const handleOneChar = useCallback(
    (val) => {
      if (!isValidFirstChar(val[0])) return "";
      return val;
    },
    [isValidFirstChar],
  );

  const handleTwoChars = useCallback(
    (val) => {
      if (val.substr(0, 2) === "00") return val.substr(0, 1);
      if (val[0] !== "0" && !isValidSecondChar(val[0], val[1]))
        return val.substr(0, 1);
      return val;
    },
    [isValidSecondChar],
  );

  const handleThreeChars = useCallback(
    (val) => {
      if (val[2] === "/") return val.substr(0, 2);
      if (isValidThirdChar(val.substr(0, 2), val[2]))
        return val.substr(0, 2) + "/" + val.substr(2);
      return val.substr(0, 2);
    },
    [isValidThirdChar],
  );

  const handleFiveChars = useCallback(
    (val) => {
      const month = val.substr(0, 2);
      const day = val.substr(3, 2);
      if (!isValidDayForMonth(month, day)) return val.substr(0, 4);
      return val;
    },
    [isValidDayForMonth],
  );

  const handleSixChars = useCallback((val) => {
    if (val[5] === "/") return val.substr(0, 5);
    return val.substr(0, 5) + "/" + val.substr(5);
  }, []);

  const handleLeapYearValidation = useCallback(
    (val) => {
      const len = val.length;
      const yearPrefix = val.substr(0, 6);

      if (yearPrefix !== "02/29/") return val;

      if (len === 8) {
        let year = +val.substr(6);
        year = year > 1970 ? 1900 + year : 2000 + year;
        if (isLeapYear(year)) return val.substr(0, 7);
      } else if (len === 10) {
        if (isLeapYear(+val.substr(6))) return val.substr(0, 9);
      }

      return val;
    },
    [isLeapYear],
  );

  const format = useCallback(
    (val) => {
      if (!val) return "";
      const len = val.length;

      if (len === 1) return handleOneChar(val);
      if (len === 2) return handleTwoChars(val);
      if (len === 3) return handleThreeChars(val);
      if (len === 5) return handleFiveChars(val);
      if (len === 6) return handleSixChars(val);
      if (len === 8 || len === 10) return handleLeapYearValidation(val);
      if (len > 10) return val.substr(0, 10);

      return val;
    },
    [
      handleOneChar,
      handleTwoChars,
      handleThreeChars,
      handleFiveChars,
      handleSixChars,
      handleLeapYearValidation,
    ],
  );

  const handleChange = (e) => {
    setDate(e);
    if (onChange) {
      onChange(e);
    }
  };

  const onChangeRaw = useCallback(
    (e) => {
      e.target.value = format(e.target.value);
    },
    [format],
  );

  const handleClickOutside = (e) => {
    if (onClickOutside) {
      onClickOutside(date);
    }
  };

  return (
    <div className="relative" style={props.style}>
      <DatePicker
        className="ant-input ant-input-md rnm-input"
        format={
          props.locale === "es" || props.locale === undefined
            ? "MM/DD/YY"
            : "DD/MM/YY"
        }
        onChange={handleChange}
        onChangeRaw={onChangeRaw}
        onClickOutside={handleClickOutside}
        selected={date}
        placeholder={
          props.locale === "es" || props.locale === undefined
            ? "dd/mm/yy"
            : "mm/dd/yy"
        }
        showMonthDropdown
        showYearDropdown
        dateFormat={
          props.locale === "es" || props.locale === undefined
            ? "dd/MM/yyyy"
            : "MM/dd/yyyy"
        }
        {...props}
      />
      <CalendarOutlined
        className="text-gray-400 absolute right-4"
        style={{ top: ".5rem" }}
      />
    </div>
  );
}
